import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSEO } from '../hooks/useSEO';
import { PageHeader } from '../components/navigation';
import { ModernCard } from '../components/ui';
import { MicroPromiseCard } from '../components/ui';
import { 
  CheckSquare, 
  Clock, 
  Target,
  AlertCircle,
  CheckCircle,
  ChevronRight,
  Calendar,
  Bell,
  Star,
  TrendingUp,
  Users,
  MessageSquare,
  X,
  Plus
} from 'lucide-react';

const NextPage: React.FC = () => {
  const navigate = useNavigate();
  const [dismissedGuidance, setDismissedGuidance] = useState<number[]>([]);

  // SEO optimization
  useSEO({
    title: 'Next Steps - MyNewStart',
    description: 'Your actionable checklists and practical tasks for your American journey.',
    keywords: ['next steps', 'checklists', 'tasks', 'progress', 'actionable'],
    breadcrumbs: [
      { name: 'Home', url: '/' },
      { name: 'Next Steps', url: '/next' }
    ]
  });

  const currentStage = {
    name: 'Getting Started',
    description: 'Setting up your foundation in America',
    progress: 35,
    color: 'blue'
  };

  const proactiveGuidance = [
    {
      id: 1,
      title: 'Tax Season is Coming!',
      message: 'Start gathering your documents now to avoid last-minute stress.',
      type: 'urgent',
      action: 'Start Tax Prep',
      deadline: 'March 15'
    },
    {
      id: 2,
      title: 'Credit Building Opportunity',
      message: 'You\'re ready to apply for your first credit card.',
      type: 'opportunity',
      action: 'Apply Now',
      deadline: null
    },
    {
      id: 3,
      title: 'Healthcare Enrollment',
      message: 'Open enrollment period is ending soon.',
      type: 'deadline',
      action: 'Enroll Now',
      deadline: 'Dec 31'
    }
  ];

  const checklistGroups = [
    {
      id: 1,
      title: 'Banking & Credit Setup',
      description: 'Essential financial foundation',
      progress: 3,
      total: 8,
      items: [
        { id: 1, title: 'Open a checking account', completed: true, priority: 'high' },
        { id: 2, title: 'Get a debit card', completed: true, priority: 'high' },
        { id: 3, title: 'Set up online banking', completed: true, priority: 'medium' },
        { id: 4, title: 'Apply for a credit card', completed: false, priority: 'high' },
        { id: 5, title: 'Set up automatic bill pay', completed: false, priority: 'medium' },
        { id: 6, title: 'Create a budget', completed: false, priority: 'high' },
        { id: 7, title: 'Set up emergency fund', completed: false, priority: 'medium' },
        { id: 8, title: 'Monitor credit score', completed: false, priority: 'low' }
      ]
    },
    {
      id: 2,
      title: 'Documentation & Legal',
      description: 'Important paperwork and legal requirements',
      progress: 2,
      total: 6,
      items: [
        { id: 1, title: 'Get Social Security Number', completed: true, priority: 'high' },
        { id: 2, title: 'Apply for state ID', completed: true, priority: 'high' },
        { id: 3, title: 'Register to vote', completed: false, priority: 'medium' },
        { id: 4, title: 'Update immigration status', completed: false, priority: 'high' },
        { id: 5, title: 'Get driver\'s license', completed: false, priority: 'medium' },
        { id: 6, title: 'File first tax return', completed: false, priority: 'high' }
      ]
    },
    {
      id: 3,
      title: 'Housing & Utilities',
      description: 'Setting up your home',
      progress: 1,
      total: 5,
      items: [
        { id: 1, title: 'Find permanent housing', completed: true, priority: 'high' },
        { id: 2, title: 'Set up electricity', completed: false, priority: 'high' },
        { id: 3, title: 'Set up internet', completed: false, priority: 'medium' },
        { id: 4, title: 'Get renters insurance', completed: false, priority: 'medium' },
        { id: 5, title: 'Register for mail forwarding', completed: false, priority: 'low' }
      ]
    }
  ];

  const reminders = [
    {
      id: 1,
      title: 'Schedule 2 learning sessions this week',
      type: 'learning',
      time: 'Today, 2:00 PM'
    },
    {
      id: 2,
      title: 'Review your budget',
      type: 'financial',
      time: 'Tomorrow, 10:00 AM'
    }
  ];

  const dismissGuidance = (id: number) => {
    setDismissedGuidance(prev => [...prev, id]);
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'text-red-600 bg-red-100';
      case 'medium': return 'text-yellow-600 bg-yellow-100';
      case 'low': return 'text-green-600 bg-green-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getGuidanceTypeColor = (type: string) => {
    switch (type) {
      case 'urgent': return 'border-red-200 bg-red-50';
      case 'opportunity': return 'border-green-200 bg-green-50';
      case 'deadline': return 'border-orange-200 bg-orange-50';
      default: return 'border-blue-200 bg-blue-50';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-20 md:pb-8">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="px-4 py-4">
          <PageHeader
            title="Next Steps"
            description="Your actionable checklists and practical tasks"
            icon={<CheckSquare className="h-6 w-6 text-green-600" />}
          />
        </div>
      </div>

      {/* Content */}
      <div className="px-4 py-6 space-y-6">
        {/* Current Stage */}
        <ModernCard className="p-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="flex items-center mb-2">
                <span className={`px-3 py-1 rounded-full text-sm font-medium bg-${currentStage.color}-100 text-${currentStage.color}-600`}>
                  {currentStage.name}
                </span>
              </div>
              <h2 className="text-lg font-semibold text-gray-900 mb-1">{currentStage.name}</h2>
              <p className="text-gray-600 text-sm">{currentStage.description}</p>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-gray-900">{currentStage.progress}%</div>
              <div className="text-sm text-gray-500">Complete</div>
            </div>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className={`bg-${currentStage.color}-600 h-2 rounded-full transition-all duration-300`}
              style={{ width: `${currentStage.progress}%` }}
            ></div>
          </div>
        </ModernCard>

        {/* Proactive Guidance */}
        {proactiveGuidance.filter(g => !dismissedGuidance.includes(g.id)).length > 0 && (
          <div className="space-y-3">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <Bell className="w-5 h-5 text-orange-500 mr-2" />
              Proactive Guidance
            </h3>
            {proactiveGuidance
              .filter(g => !dismissedGuidance.includes(g.id))
              .map((guidance) => (
                <ModernCard key={guidance.id} className={`p-4 border-l-4 ${getGuidanceTypeColor(guidance.type)}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center mb-2">
                        <h4 className="font-semibold text-gray-900 mr-2">{guidance.title}</h4>
                        {guidance.deadline && (
                          <span className="text-xs font-medium text-orange-600 bg-orange-100 px-2 py-1 rounded-full">
                            Due {guidance.deadline}
                          </span>
                        )}
                      </div>
                      <p className="text-gray-600 text-sm mb-3">{guidance.message}</p>
                      <button className="bg-blue-600 text-white py-2 px-4 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors">
                        {guidance.action}
                      </button>
                    </div>
                    <button 
                      onClick={() => dismissGuidance(guidance.id)}
                      className="ml-3 p-1 text-gray-400 hover:text-gray-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </ModernCard>
              ))}
          </div>
        )}

        {/* Checklist Groups */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">Your Action Items</h3>
          {checklistGroups.map((group) => (
            <ModernCard key={group.id} className="p-4">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">{group.title}</h4>
                  <p className="text-gray-600 text-sm">{group.description}</p>
                </div>
                <div className="text-right">
                  <div className="text-lg font-bold text-gray-900">{group.progress}/{group.total}</div>
                  <div className="text-sm text-gray-500">Complete</div>
                </div>
              </div>
              
              <div className="space-y-3">
                {group.items.slice(0, 3).map((item) => (
                  <div key={item.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center">
                      <button className={`w-5 h-5 rounded border-2 flex items-center justify-center mr-3 ${
                        item.completed 
                          ? 'bg-green-500 border-green-500 text-white' 
                          : 'border-gray-300 hover:border-green-500'
                      }`}>
                        {item.completed && <CheckCircle className="w-3 h-3" />}
                      </button>
                      <div>
                        <span className={`text-sm font-medium ${item.completed ? 'line-through text-gray-500' : 'text-gray-900'}`}>
                          {item.title}
                        </span>
                        <span className={`ml-2 text-xs px-2 py-1 rounded-full ${getPriorityColor(item.priority)}`}>
                          {item.priority}
                        </span>
                      </div>
                    </div>
                    {!item.completed && (
                      <button className="text-blue-600 text-sm font-medium hover:text-blue-700">
                        Start
                      </button>
                    )}
                  </div>
                ))}
                
                {group.items.length > 3 && (
                  <button className="w-full py-2 text-blue-600 font-medium border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors flex items-center justify-center">
                    View All {group.total} Items
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </button>
                )}
              </div>
            </ModernCard>
          ))}
        </div>

        {/* Reminders */}
        <div className="space-y-3">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <Calendar className="w-5 h-5 text-blue-500 mr-2" />
            Reminders
          </h3>
          {reminders.map((reminder) => (
            <ModernCard key={reminder.id} className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                    <Clock className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900">{reminder.title}</h4>
                    <p className="text-sm text-gray-500">{reminder.time}</p>
                  </div>
                </div>
                <button className="p-2 text-gray-400 hover:text-gray-600">
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </ModernCard>
          ))}
        </div>

        {/* Quick Actions */}
        <div className="space-y-3">
          <h3 className="text-lg font-semibold text-gray-900">Quick Actions</h3>
          <div className="grid grid-cols-2 gap-3">
            <button className="p-4 bg-white rounded-xl border border-gray-200 hover:border-blue-300 hover:shadow-md transition-all duration-200 text-left">
              <div className="flex items-center justify-between mb-2">
                <Plus className="w-5 h-5 text-blue-600" />
                <ChevronRight className="w-4 h-4 text-gray-400" />
              </div>
              <h4 className="font-medium text-gray-900 mb-1">Add Custom Task</h4>
              <p className="text-sm text-gray-500">Create your own action item</p>
            </button>
            <button className="p-4 bg-white rounded-xl border border-gray-200 hover:border-blue-300 hover:shadow-md transition-all duration-200 text-left">
              <div className="flex items-center justify-between mb-2">
                <Calendar className="w-5 h-5 text-green-600" />
                <ChevronRight className="w-4 h-4 text-gray-400" />
              </div>
              <h4 className="font-medium text-gray-900 mb-1">Schedule Session</h4>
              <p className="text-sm text-gray-500">Plan your learning time</p>
            </button>
          </div>
        </div>
      </div>

      {/* Floating Action Button for Mini Angel */}
      <button 
        onClick={() => navigate('/chat')}
        className="fixed bottom-28 right-4 md:bottom-8 bg-blue-600 text-white p-4 rounded-full shadow-lg hover:bg-blue-700 transition-colors z-40"
      >
        <MessageSquare className="w-6 h-6" />
      </button>
    </div>
  );
};

export default NextPage;
